<template>
  <div class="layer-order-field">
    <button
      type="button"
      class="order-button"
      :disabled="!hasCategory || loading"
      @click="show"
    >
      <font-awesome-icon icon="sort" />
      Definir orden
    </button>

    <small v-if="!hasCategory" class="order-unavailable">
      El orden no está disponible hasta seleccionar una categoría.
    </small>
    <small v-else-if="normalizedValue > 0" class="order-selected">
      Posición seleccionada: {{ normalizedValue }} dentro de {{ categoryName || 'la categoría' }}.
    </small>
    <small v-else class="order-help">
      Si no define otro orden, la capa quedará al final de la categoría.
    </small>

    <modal
      name="LayerOrderFormModal"
      :width="'90%'"
      :max-width="680"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      class="layer-order-vmodal"
      @closed="resetModal"
    >
      <section class="layer-order-modal" aria-labelledby="form-layer-order-title">
        <header class="layer-order-header">
          <div>
            <h2 id="form-layer-order-title">Definir orden de capas</h2>
            <p>Ubique la capa actual y, si lo necesita, reordene las demás capas de esta categoría.</p>
          </div>
          <button type="button" class="close-button" title="Cerrar" @click="close">&times;</button>
        </header>

        <div class="layer-order-body">
          <label class="category-field">
            Categoría seleccionada
            <input type="text" :value="categoryName || 'Categoría seleccionada'" disabled />
          </label>

          <div v-if="loading" class="layer-order-state">
            <font-awesome-icon icon="spinner" spin />
            <span>Cargando capas...</span>
          </div>
          <div v-else-if="errorMessage" class="layer-order-error" role="alert">
            {{ errorMessage }}
          </div>

          <ol v-else class="layer-order-list">
            <li
              v-for="(layer, index) in orderedLayers"
              :key="layer.id"
              :class="{ dragging: draggedIndex === index, 'current-layer': layer.isCurrent }"
              draggable="true"
              @dragstart="startDrag(index, $event)"
              @dragover.prevent
              @drop="dropLayer(index)"
              @dragend="draggedIndex = null"
            >
              <span class="layer-position">{{ index + 1 }}</span>
              <span class="drag-handle" title="Arrastrar para reordenar"><font-awesome-icon icon="sort" /></span>
              <span class="layer-name">
                {{ layer.name }}
                <span v-if="layer.isCurrent" class="current-badge">Capa actual</span>
              </span>
              <div class="layer-order-controls">
                <button type="button" title="Subir capa" :disabled="index === 0" @click="moveLayer(index, -1)">
                  <font-awesome-icon icon="arrow-up" />
                </button>
                <button type="button" title="Bajar capa" :disabled="index === orderedLayers.length - 1" @click="moveLayer(index, 1)">
                  <font-awesome-icon icon="arrow-down" />
                </button>
              </div>
            </li>
          </ol>
          <p v-if="!loading && !errorMessage" class="save-note">
            Este orden se guardará cuando cree o actualice la capa.
          </p>
        </div>

        <footer class="layer-order-footer">
          <button type="button" class="button-secondary" @click="close">Cancelar</button>
          <button type="button" class="button-primary" :disabled="loading || !!errorMessage" @click="applyOrder">
            Aplicar orden
          </button>
        </footer>
      </section>
    </modal>
  </div>
</template>

<script>
import { restLayer } from '@/components/mixin/restLayer.js'

const CURRENT_LAYER_ID = '__current_layer__'

export default {
  name: 'LayerOrderFormModal',
  mixins: [restLayer],
  props: {
    categoryId: { type: String, default: '' },
    categoryName: { type: String, default: '' },
    currentLayerId: { type: String, default: '' },
    currentLayerName: { type: String, default: '' },
    value: { type: [Number, String], default: 0 },
    layerOrderIds: { type: Array, default: null }
  },
  data() {
    return {
      orderedLayers: [],
      loading: false,
      errorMessage: '',
      draggedIndex: null,
      activeCategory: '',
      initializedCategory: false,
      requestSequence: 0
    }
  },
  computed: {
    hasCategory() {
      return !!this.categoryId && this.categoryId !== '-1'
    },
    normalizedValue() {
      const value = Number(this.value)
      return Number.isInteger(value) && value > 0 ? value : 0
    },
    displayCurrentLayerName() {
      const name = (this.currentLayerName || '').trim()
      if (name) return name
      return this.currentLayerId ? 'Capa en edición' : 'Nueva capa (sin nombre)'
    }
  },
  watch: {
    categoryId: {
      immediate: true,
      handler(newCategory) {
        const validCategory = newCategory && newCategory !== '-1' ? newCategory : ''
        if (!validCategory) {
          this.activeCategory = ''
          this.clearAppliedOrder()
          return
        }

        if (!this.initializedCategory) {
          this.initializedCategory = true
          this.activeCategory = validCategory
          return
        }

        if (validCategory !== this.activeCategory) {
          this.activeCategory = validCategory
          this.clearAppliedOrder()
        }
      }
    }
  },
  methods: {
    show() {
      if (!this.hasCategory || this.loading) return
      const requestID = ++this.requestSequence
      this.loading = true
      this.errorMessage = ''
      this.orderedLayers = []
      this.$modal.show('LayerOrderFormModal')

      this.getLayersForOrder(this.categoryId, this.currentLayerId)
        .then(layers => {
          if (requestID !== this.requestSequence) return
          this.orderedLayers = this.buildOrder(layers)
        })
        .catch(() => {
          if (requestID !== this.requestSequence) return
          this.errorMessage = 'No fue posible cargar las capas de la categoría seleccionada.'
        })
        .then(() => {
          if (requestID === this.requestSequence) this.loading = false
        })
    },
    buildOrder(layers) {
      const availableLayers = (layers || []).map(layer => ({ ...layer, isCurrent: false }))
      let existingLayers = availableLayers

      if (Array.isArray(this.layerOrderIds)) {
        const byID = new Map(availableLayers.map(layer => [layer.id, layer]))
        existingLayers = this.layerOrderIds.map(id => byID.get(id)).filter(Boolean)
        availableLayers.forEach(layer => {
          if (!existingLayers.some(item => item.id === layer.id)) existingLayers.push(layer)
        })
      }

      const currentLayer = {
        id: CURRENT_LAYER_ID,
        name: this.displayCurrentLayerName,
        isCurrent: true
      }
      const requestedPosition = this.normalizedValue
      const insertionIndex = requestedPosition > 0 && requestedPosition <= existingLayers.length + 1
        ? requestedPosition - 1
        : existingLayers.length
      const result = existingLayers.slice()
      result.splice(insertionIndex, 0, currentLayer)
      return result
    },
    clearAppliedOrder() {
      this.requestSequence++
      this.orderedLayers = []
      this.loading = false
      this.errorMessage = ''
      if (this.normalizedValue !== 0) this.$emit('input', 0)
      this.$emit('order-change', null)
    },
    resetModal() {
      this.requestSequence++
      this.orderedLayers = []
      this.loading = false
      this.errorMessage = ''
      this.draggedIndex = null
    },
    close() {
      this.$modal.hide('LayerOrderFormModal')
    },
    startDrag(index, event) {
      this.draggedIndex = index
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', String(index))
    },
    dropLayer(targetIndex) {
      if (this.draggedIndex === null || this.draggedIndex === targetIndex) return
      const movedLayer = this.orderedLayers.splice(this.draggedIndex, 1)[0]
      this.orderedLayers.splice(targetIndex, 0, movedLayer)
      this.draggedIndex = null
    },
    moveLayer(index, direction) {
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= this.orderedLayers.length) return
      const movedLayer = this.orderedLayers.splice(index, 1)[0]
      this.orderedLayers.splice(targetIndex, 0, movedLayer)
    },
    applyOrder() {
      const currentIndex = this.orderedLayers.findIndex(layer => layer.isCurrent)
      if (currentIndex < 0) return
      const existingLayerIDs = this.orderedLayers
        .filter(layer => !layer.isCurrent)
        .map(layer => layer.id)
      this.$emit('input', currentIndex + 1)
      this.$emit('order-change', existingLayerIDs)
      this.close()
    }
  }
}
</script>

<style lang="scss" scoped>
.layer-order-field { width: 100%; }
.order-button { display: inline-flex; align-items: center; gap: .45rem; min-height: 36px; padding: .45rem .8rem; border: 1px solid var(--color-navy); border-radius: 4px; background: #fff; color: var(--color-navy); font-weight: 600; cursor: pointer; }
.order-button:hover:not(:disabled) { border-color: var(--color-orange); color: var(--color-orange); }
.order-button:disabled { border-color: #cbd6de; background: #edf1f3; color: #8a98a3; cursor: not-allowed; }
.order-help,.order-selected,.order-unavailable { display: block; margin-top: .35rem; font-size: .76rem; }
.order-help { color: #667786; }
.order-selected { color: #24633b; }
.order-unavailable { color: #922b21; }
.layer-order-vmodal::v-deep .vm--modal { background: transparent; box-shadow: none; }
.layer-order-modal { overflow: hidden; width: 100%; max-height: 88vh; border-radius: 8px; background: #fff; box-shadow: 0 18px 48px rgba(15,42,61,.28); color: var(--color-navy); }
.layer-order-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; padding: 1.25rem 1.4rem; border-bottom: 1px solid #dfe6eb; }
.layer-order-header h2 { margin: 0 0 .35rem; font-size: 1.15rem; }
.layer-order-header p { margin: 0; color: #667786; font-size: .82rem; line-height: 1.45; }
.close-button { flex: 0 0 auto; width: 30px; height: 30px; border: 0; border-radius: 4px; background: transparent; color: #667786; font-size: 1.4rem; cursor: pointer; }
.close-button:hover { background: #edf2f5; color: var(--color-navy); }
.layer-order-body { overflow-y: auto; max-height: 60vh; padding: 1rem 1.4rem; background: #f7f9fa; }
.category-field { display: flex; flex-direction: column; gap: .35rem; margin-bottom: .9rem; color: #32485a; font-size: .82rem; font-weight: 600; }
.category-field input { width: 100%; padding: .5rem .6rem; border: 1px solid #cbd6de; border-radius: 4px; background: #edf1f3; color: #526574; }
.layer-order-state { display: flex; align-items: center; justify-content: center; gap: .6rem; min-height: 120px; color: #667786; }
.layer-order-list { display: flex; flex-direction: column; gap: .45rem; margin: 0; padding: 0; list-style: none; }
.layer-order-list li { display: grid; grid-template-columns: 32px 24px minmax(0,1fr) auto; align-items: center; gap: .55rem; min-height: 44px; padding: .45rem .55rem; border: 1px solid #d7e0e6; border-radius: 5px; background: #fff; cursor: grab; }
.layer-order-list li.current-layer { border-color: var(--color-orange); background: #fff3e7; box-shadow: inset 3px 0 0 var(--color-orange); }
.layer-order-list li.dragging { opacity: .5; }
.layer-position { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 4px; background: #edf2f5; font-size: .78rem; font-weight: 700; }
.drag-handle { color: #8a9aa7; text-align: center; }
.layer-name { overflow: hidden; color: #32485a; font-size: .86rem; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.current-badge { display: inline-block; margin-left: .45rem; padding: .12rem .35rem; border-radius: 3px; background: var(--color-orange); color: #fff; font-size: .66rem; vertical-align: middle; }
.layer-order-controls { display: flex; gap: .35rem; }
.layer-order-controls button { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border: 1px solid #cbd6de; border-radius: 4px; background: #fff; color: var(--color-navy); cursor: pointer; }
.layer-order-controls button:hover:not(:disabled) { border-color: var(--color-orange); color: var(--color-orange); }
.layer-order-controls button:disabled { opacity: .4; cursor: not-allowed; }
.layer-order-error { padding: .65rem .8rem; border: 1px solid #edb9b5; border-radius: 4px; background: #fff1f0; color: #922b21; font-size: .82rem; }
.save-note { margin: .75rem 0 0; color: #667786; font-size: .76rem; text-align: right; }
.layer-order-footer { display: flex; justify-content: flex-end; gap: .6rem; padding: 1rem 1.4rem; border-top: 1px solid #dfe6eb; background: #fff; }
.layer-order-footer button { min-height: 36px; padding: .45rem .9rem; border-radius: 4px; font-size: .82rem; font-weight: 600; cursor: pointer; }
.button-secondary { border: 1px solid #cbd6de; background: #fff; color: var(--color-navy); }
.button-primary { border: 1px solid var(--color-navy); background: var(--color-navy); color: #fff; }
.button-primary:disabled { opacity: .5; cursor: not-allowed; }
@media (max-width: 520px) { .layer-order-list li { grid-template-columns: 28px 20px minmax(0,1fr); } .layer-order-controls { grid-column: 1 / -1; justify-content: flex-end; } }
</style>
